import { mapState, mapGetters } from 'vuex';
import ApiService from '../../../services/apiService';
import { EventBus } from '../../../services/eventbus.js';
// import { logMessage } from '../../../sentry';
import { dtwinTreeCacheKey } from './tree/mixin';
import { validImageFileName } from '../../../utils/vuex';
import SocketMixin from './socketMixin';
import unsavedMixin from '../unsavedMixin';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';

export default {
  mixins: [SocketMixin, unsavedMixin],
  data: function () {
    return {
      baseLink: 'digitaltwin-mgmt',
      // if used for virtual-devices, change the get-method to obtain a virtual-device rather than a twin
      apiMethodGet: 'digitalTwinGet',
      apiMethodUpdate: 'digitalTwinUpdate',
      apiMethodToggleDisabled: 'digitalTwinUpdateDisabled',
      permissionSubject: 'DigitalTwin',
      notFoundError: 'Common.digitalTwins.twinNotFound',

      isLoading: true,
      isLoadingStates: true,
      exportDialog: false,
      disableDialog: false,

      dtwinUsers: [],

      valid: false,
      originalTwinObject: null,
      twinObject: {
        title: null,
        parentId: null,
        tenantId: null,
        data: {
          description: null,
          faIcon: null,
          tags: [],
          address: null,
          indoor: {},
          events: [],
          contacts: [],
          isFm: {
            floor: null,
            room: null,
            doorId: null,
          },
        },
        properties: {},
        coordinates: { coordinates: [null, null] },
        polygon: { coordinates: [] },
      },
      deviceTemplate: null,
      // if the parent is changed, clear the whole twin-tree-cache
      originalParentId: null,
      hasVisibleParent: false,
      parentIsDisabled: true,
      hasFloorTwinReadPermissions: false,
      floorTwinOverlayData: null,
      // is the user allowed to just read or also edit -- this particular digitaltwin, that is
      hasTwinManagePermission: false,
      hasTwinEditPermission: false,
      hasUserReadPermission: false,

      twinDataStates: [],
      updatedStates: [],
      twinDataStatesError: false,

      parentTree: null,
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    ...mapGetters({
      sessionUserId: 'authentication/userId',
    }),
    wrappedInstance() {
      // to be used in vue-template if need to refer to "this" explicitly
      return this;
    },
    requiredRule() {
      return [(v) => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    twinImages() {
      return [{ name: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataSources.none'), id: null }].concat(
        ((this.twinObject.data && this.twinObject.data.attachments) || []).filter((attachment) => {
          return validImageFileName(attachment.name);
        })
      );
    },
    twinCanBeEdited() {
      return this.hasTwinEditPermission && !this.dtwinIsDisabled;
    },
    dtwinIsDisabled() {
      return get(this.twinObject, 'properties.disabled') === true;
    },
    dtwinTypeValidForIsFm() {
      if (this.twinObject.data && this.twinObject.data.isFm) {
        switch (this.twinObject.data.type) {
          case 'zone':
          case 'trackable':
            return false;
          default:
            return true;
        }
      }
      return false;
    },
    saveButton() {
      return this.$i18n_t('Common.save');
    },
    activeId() {
      if (this.$route.params.id) {
        return this.$route.params.id;
      }
      return this.$route.query.parent;
    },
    openTree() {
      return this.$route.query.openTree;
    },
    cloading() {
      return this.$store.state.app.loading;
    },
  },
  watch: {
    'twinObject.floorDtwinId'() {
      this.checkFloorTwinPermission();
    },
    twinObject: {
      handler() {
        if (this.originalTwinObject === null) {
          return;
        }

        if (this.twinObject.id !== this.originalTwinObject.id) {
          return;
        }

        // NOTE: coordiantes and polycon could contain irrelephant meta-information like
        // {..."type": "polygon"} that is added or removed by UI components
        // ignore those and only focus on the actual coordinates of those
        const coordinatesEqual = isEqual(
          get(this.originalTwinObject, 'coordinates.coordinates'),
          get(this.twinObject, 'coordinates.coordinates')
        );
        if (!coordinatesEqual) {
          this.formIsDirty = true;
          return;
        }
        const polygonEqual = isEqual(
          get(this.originalTwinObject, 'polygon.coordinates'),
          get(this.twinObject, 'polygon.coordinates')
        );
        if (!polygonEqual) {
          this.formIsDirty = true;
          return;
        }
        // coordinates and polygon are sufficiently equal, compare the rest of the twin-data
        const orgTwinObj = { ...this.originalTwinObject, children: null, coordinates: null, polygon: null };
        const twinObj = { ...this.twinObject, children: null, coordinates: null, polygon: null };

        if (!orgTwinObj.data.tags || !isArray(orgTwinObj.data.tags)) {
          if (twinObj.data.tags && isArray(twinObj.data.tags)) {
            orgTwinObj.data.tags = [];
          }
        }
        if (orgTwinObj.data.description === undefined && twinObj.data.description !== undefined) {
          orgTwinObj.data.description = twinObj.data.description;
        }
        this.formIsDirty = !isEqual(orgTwinObj, twinObj);
        // for debugging purposes, one may un-comment this to find out which item changed, to identify "weird" behaviour
        // prints out all things that changed
        // if (this.formIsDirty) {
        //   Object.keys(orgTwinObj).forEach((k1) => {
        //     if (!isEqual(orgTwinObj[k1], twinObj[k1])) {
        //       if (twinObj[k1].constructor === Object) {
        //         Object.keys(orgTwinObj[k1]).forEach((k2) => {
        //           if (!isEqual(orgTwinObj[k1][k2], twinObj[k1][k2])) {
        //             console.log('changed twin data:', `${k1}.${k2}`, orgTwinObj[k1][k2], twinObj[k1][k2]);
        //           }
        //         });
        //       } else {
        //         console.log('changed twin data:', `${k1}`, orgTwinObj[k1], twinObj[k1]);
        //       }
        //     }
        //   });
        // }
      },
      deep: true,
    },
  },
  methods: {
    get,
    beforeWindowUnload(e) {
      if (this.formIsDirty) {
        // Cancel the event
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        // eslint-disable-next-line no-empty-function
        this.unsavePromptNextPersist = () => {};
        this.unsavedPrompt = true;
      }
    },
    beforeRouteLeave(to, from, next) {
      // If the form is dirty and the user did not confirm leave,
      // prevent losing unsaved changes by canceling navigation
      if (this.formIsDirty) {
        this.unsavePromptNextPersist = next;
        this.unsavedPrompt = true;
      } else {
        next();
      }
    },
    statesValuesUpdated(statesData) {
      statesData.forEach((stateData) => {
        const state = (this.twinDataStates || []).find((s) => parseInt(s.id) === parseInt(stateData.id));
        set(state, 'value', stateData);
      });
      this.updatedStates = statesData;
      this.twinDataStates = cloneDeep(this.twinDataStates);
    },
    setup() {
      this.$store.state.app.load = 10;
      this.$store.state.app.loading = true;
      this.isLoading = true;

      this.setupDtwinSocket(this.$route.params.id);
      ApiService[this.apiMethodGet](this, this.$route.params.id)
        .then((data) => {
          this.isLoading = false;
          this.twinObject = data;

          const templateId = this.twinObject.deviceTemplateId;
          if (templateId) {
            ApiService.deviceTemplateGetByIdDraft(templateId)
              .then((deviceTemplateObject) => {
                this.deviceTemplate = deviceTemplateObject;
              })
              .catch((error) => {
                console.error('Could not get device template', error);
                this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.getError'), 'error');
              });
          }

          // RD-5957: reset deviceTypeSync for devices already affected from prior bug
          if (get(this.twinObject, 'data.virtualDevice')) {
            delete this.twinObject.data.virtualDevice.deviceTypeSync;
          }

          this.prepareTwinObject(this.twinObject);
          return this.checkAncestorsAndPermissions().then(() => {
            this.prepareTwinObject(this.twinObject);
            // call an optional method on the main view to trigger further custom handlings
            // e.g. for a twin-detail-view in modules
            if (this['finishedTwinSetup']) {
              this['finishedTwinSetup']();
            }
            if (get(this.originalTwinObject, 'id') !== get(this.twinObject, 'id')) {
              this.originalTwinObject = cloneDeep(this.twinObject);
            }
            EventBus.$emit('twin-finished-fetching');
            const rulesTable = this.$refs['rulesTable'];
            if (rulesTable) {
              rulesTable.fetchTwinRules();
            }
            return this.twinObject;
          });
        })
        .catch((error) => {
          console.error('Could not init digital twin', error);

          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t(this.notFoundError), 'error');
          }
          this.$router.push({ path: `/${this.baseLink}`, query: { active: this.activeId } });
        })
        .finally(() => {
          this.$store.state.app.loading = false;
        });
      this.refreshStates();
    },

    prepareTwinObject(dtwin) {
      if (!dtwin.properties) {
        dtwin.properties = {};
      }
      if (!dtwin.data) {
        dtwin.data = {};
      }
      if (!dtwin.data.keyValue) {
        dtwin.data.keyValue = {};
      }
      // make sure the tags are actually an array
      if (!isArray(dtwin.data.tags)) {
        dtwin.data.tags = [];
      }
      if (!dtwin.data.description) {
        dtwin.data.description = '';
      }
      if (!dtwin.data.indoor) {
        dtwin.data.indoor = {};
      }
      if (!dtwin.data.isFm) {
        dtwin.data.isFm = {};
      }
      if (!dtwin.data.faIcon) {
        dtwin.data.faIcon = null;
      }
      if (!get(dtwin, 'coordinates.coordinates')) {
        set(dtwin, 'coordinates.coordinates', [null, null]);
      }
      if (!get(dtwin, 'polygon.coordinates')) {
        dtwin.polygon = { coordinates: [] };
      }
      // will trigger the geo-mixin to do stuff
      dtwin.polygon = cloneDeep(dtwin.polygon);
      this.originalParentId = dtwin.parentId;

      this.setupGeoData();
    },

    getParentFromQuery() {
      if (this.$route.query.parent) {
        this.twinObject.parentId = parseInt(this.$route.query.parent);
      }
    },

    getTenantId() {
      if (this.twinObject.tenantId) {
        return this.twinObject.tenantId;
      }

      ApiService[this.apiMethodGet](this, this.twinObject.parentId)
        .then((parentTwin) => {
          this.twinObject.tenantId = parentTwin.tenantId;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    ancestorList() {
      const extract = (tree) => {
        if (tree.children && tree.children.length > 0) {
          return [tree].concat(extract(tree.children[0]));
        }
        return [tree];
      };
      if (this.parentTree) {
        return cloneDeep(
          extract(this.parentTree)
            .filter((i) => i && i.id)
            .concat([this.twinObject])
        );
      }
      return [];
    },

    showExportModal() {
      this.exportDialog = true;
    },
    editUser(user) {
      this.$router.push(`/account-mgmt/user/${this.twinObject.tenantId}/${user ? user.id : ''}`);
    },
    scopeFetchUsers(scopeId) {
      if (this.hasUserReadPermission && scopeId) {
        ApiService.fetchUsers([scopeId], ['ancestors'])
          .then((users) => {
            this.dtwinUsers = users;
          })
          .catch((error) => {
            console.error(error);
            if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
              this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
            } else {
              this.$snack('Failed to fetch scopes for user', 'error');
            }
          });
      }
    },

    async checkFloorTwinPermission() {
      this.hasFloorTwinReadPermissions = false;
      this.floorTwinOverlayData = {};
      let floorTwinPromise = this.twinObject.floorDtwinId
        ? ApiService.digitalTwinGet(this, this.twinObject.floorDtwinId)
        : Promise.resolve({});

      const floorTwinResult = await floorTwinPromise.catch((error) => {
        console.error(error);
      });
      if (floorTwinResult) {
        this.floorTwinOverlayData = floorTwinResult;
        this.hasFloorTwinReadPermissions = true;
      }
    },

    checkAncestorsAndPermissions() {
      let parentPartialTreePromise = this.twinObject.parentId
        ? ApiService.digitalTwinFetchPartialTree(this, this.twinObject.parentId, undefined, undefined, ['no-children'])
        : Promise.resolve({});
      let parentObjectPromise = this.twinObject.parentId
        ? ApiService.digitalTwinGet(this, this.twinObject.parentId, true)
        : Promise.resolve({});

      return Promise.all([
        // fetch concrete permissions for the scope of the dtwin
        ApiService.checkAccesses(
          [
            { subject: this.permissionSubject, action: 'read' },
            { subject: this.permissionSubject, action: 'write' },
            { subject: this.permissionSubject, action: 'manage' },
            { subject: 'UserManagement', action: 'read' },
          ],
          { scopeId: this.twinObject.scopeId }
        ),
        // for a breadcrum-path, fetch the partial tree up to this twin
        parentPartialTreePromise,
      ])
        .then((result) => {
          const permissions = result[0];
          this.hasTwinManagePermission = permissions[`${this.permissionSubject}.manage`];
          this.hasTwinEditPermission = permissions[`${this.permissionSubject}.write`];
          this.hasUserReadPermission = permissions['UserManagement.read'];
          // user has not even read-permissions for this twin, do not allow that motherbrother on this page
          if (!permissions[`${this.permissionSubject}.read`]) {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinNotFound'), 'error');
            this.$router.push({ path: `/${this.baseLink}`, query: { active: this.activeId } });
          }

          const tree = result[1];
          this.parentTree = get(tree, 'digitaltwins[0]', {});
          EventBus.$emit('update-breadcrumbs');

          // get the parent twin to check if it is accessible
          return parentObjectPromise;
        })
        .then(async (result) => {
          const parentTwin = result;
          if (this.hasUserReadPermission) {
            if (!this.twinObject.scopeId) {
              this.scopeFetchUsers(parentTwin.scopeId);
            } else {
              this.scopeFetchUsers(this.twinObject.scopeId);
            }
          }
          this.hasVisibleParent = !!parentTwin.id;
          this.parentIsDisabled = get(parentTwin, 'properties.disabled', false);

          await this.checkFloorTwinPermission();
        })
        .catch(() => {
          this.hasVisibleParent = false;
        });
    },

    dtwinToggleState() {
      set(this.twinObject, 'properties.disabled', !this.twinObject.properties.disabled);
      this.twinObject.properties = cloneDeep(this.twinObject.properties);

      return ApiService[this.apiMethodToggleDisabled](this, this.twinObject.id, this.twinObject.properties.disabled)
        .then(() => {
          this.originalTwinObject = cloneDeep(this.twinObject);
          this.formIsDirty = false;
          this.$store.state.app.dtwins = {};
          return this.refreshStates();
        })
        .catch((error) => {
          // reset to original disabled-state
          set(this.twinObject, 'properties.disabled', !this.twinObject.properties.disabled);
          this.twinObject.properties = cloneDeep(this.twinObject.properties);
          this.originalTwinObject = cloneDeep(this.twinObject);
          this.formIsDirty = false;

          console.error(error);
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinUpdatedFail'), 'error');
          }
        });
    },

    setTags(a) {
      this.twinObject.data.tags = a.tags;
    },

    validate() {
      this.valid = this.$refs.form.validate();
      if (!this.valid) {
        this.$snack('Please fill out all required input fields', 'error');
      }
      return this.valid;
    },

    handleSubmit(forward = true) {
      this.$store.state.app.load = 10;
      if (!this.validate()) {
        return Promise.resolve();
      }
      const twinPayload = cloneDeep(this.twinObject);
      twinPayload.parentId = parseInt(twinPayload.parentId);

      const children = twinPayload.children;

      // polygon is set server-side and adapted to the current coordinate
      if (twinPayload.data.polygonMode === 'auto') {
        twinPayload.polygon = null;
      }
      twinPayload.coordinates = {
        coordinates: (get(twinPayload, 'coordinates.coordinates') || []).filter((c) => !isNaN(parseFloat(c))),
      };
      if (twinPayload.coordinates.coordinates.length < 2) {
        delete twinPayload.coordinates;
      }

      this.$store.state.app.loading = true;
      return ApiService[this.apiMethodUpdate](this, twinPayload)
        .then((result) => {
          this.formIsDirty = false;
          let successMsg =
            this.baseLink === 'virtual-devices'
              ? this.$i18n_t('DigitalTwin.VirtualDevice.details.virtualDeviceUpdated')
              : this.$i18n_t('Common.digitalTwins.twinUpdated');
          this.$snack(successMsg, 'success');
          result.children = children;
          this.updateDtwinCache(result);
          if (forward) {
            this.$router.push({
              path: `/${this.baseLink}`,
              query: { active: this.twinObject.id },
            });
          } else {
            this.twinObject = result;
            this.originalTwinObject = cloneDeep(result);
            this.twinObject.children = children;
          }
          return result;
        })
        .catch((error) => {
          console.error(error);
          if (get(error, 'response.data.message')) {
            if (this.$i18n_t(`Error.${error.response.data.message}`)) {
              this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
            } else {
              this.baseLink === 'virtual-devices'
                ? this.$snack(
                    `${this.$i18n_t('DigitalTwin.VirtualDevice.details.virtualDeviceUpdatedFail')} ${
                      error.response.data.message
                    }`,
                    'error'
                  )
                : this.$snack(
                    `${this.$i18n_t('Common.digitalTwins.twinUpdatedFail')} ${error.response.data.message}`,
                    'error'
                  );
            }
          } else {
            this.baseLink === 'virtual-devices'
              ? this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.details.virtualDeviceUpdatedFail'), 'error')
              : this.$snack(this.$i18n_t('Common.digitalTwins.twinUpdatedFail'), 'error');
          }
        })
        .finally(() => {
          this.$store.state.app.loading = false;
        });
    },

    updateDtwinCache(dtwin) {
      // update the cached twin-tree, so it has the updated information
      // clear all other twin-caches tho

      if (this.originalParentId !== dtwin.parentId) {
        // the parent changed, so clear the entire cache of twins, so the tree gets updated at once
        this.$store.state.app.dtwins = {};
        return;
      }

      const findRequestedDtwinIndexPath = (id, dtwins) => {
        if (!dtwins) {
          return null;
        }
        const index = dtwins.findIndex((d) => d.id === id);
        if (index >= 0) {
          return [index];
        }
        const indices = dtwins
          .map((d, i) => {
            const res = findRequestedDtwinIndexPath(id, d.children);
            if (!res) {
              return null;
            }
            return [i].concat(res);
          })
          .filter((d) => !!d);
        return get(indices, '[0]', null);
      };
      const cachedDtwins = get(this.$store.state.app, `dtwins.${dtwinTreeCacheKey}.twinTreeItems`);
      const dtwinIndexPath = findRequestedDtwinIndexPath(dtwin.id, cachedDtwins);
      if (dtwinIndexPath) {
        const pathKey = dtwinIndexPath.map((i) => `[${i}]`).join('.children');
        const orgCachedDtwin = get(cachedDtwins, pathKey);
        // update each property of the original cached twin, except the children
        Object.keys(dtwin)
          .filter((k) => k !== 'children')
          .forEach((key) => {
            orgCachedDtwin[key] = dtwin[key];
          });

        // reset all other caches
        Object.keys(this.$store.state.app.dtwins)
          .filter((k) => k !== dtwinTreeCacheKey)
          .forEach((cacheKey) => {
            delete this.$store.state.app.dtwins[cacheKey];
          });
      } else {
        // the item could not be found, so reset the entire twin cache
        this.$store.state.app.dtwins = {};
      }
    },

    refreshStates() {
      this.isLoadingStates = true;
      this.blockStateOrdering = true;

      return ApiService.digitalTwinFetchStates(this, this.$route.params.id)
        .then((twinDataStates) => {
          this.twinDataStates = twinDataStates.map((state) => {
            return Object.assign({}, state, {
              title: state.properties.title,
              unit: state.properties.unit,
              stateValue: state.value ? state.value.value : null,
              stateTime: state.value ? state.value.time : null,
            });
          });

          // Sort states, create order array and write back order array to database
          const sortedStates = sortBy(this.twinDataStates, 'properties.orderIdx', 'createdAt');
          // only if the order actually changed
          if (!isEqual(sortedStates, this.twinDataStates)) {
            const orderArray = sortedStates.map((item) => item.id);
            return ApiService.digitalTwinUpdateStatesOrder(this, this.$route.params.id, orderArray).catch((error) => {
              console.error(error);
            });
          }
        })
        .catch((error) => {
          // logMessage(`Twin states failed to load. ${error.toString()}`);
          console.error('Loading twins failed', error);
          this.$snack(this.$i18n_t('Common.digitalTwins.errorLoadingStates'), 'error');
          this.twinDataStates = [];
        })
        .finally(() => {
          this.blockStateOrdering = false;
          this.isLoadingStates = false;
        });
    },

    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('L LTS');
      }
      return '-';
    },
  },
};
